
import React, { useState } from 'react';
import { Courier } from '@/services/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription, 
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CourierDetailsHeader } from './CourierDetailsHeader';
import { CourierDetailsCard } from './CourierDetailsCard';
import { RentalHistoryTab } from './RentalHistoryTab';
import { PaymentHistoryTab } from './PaymentHistoryTab';
import { Trash2 } from 'lucide-react';

interface CourierDetailsDialogProps {
  courier: Courier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleteCourier?: (courier: Courier) => void;
}

export const CourierDetailsDialog: React.FC<CourierDetailsDialogProps> = ({
  courier,
  open,
  onOpenChange,
  onDeleteCourier
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    if (onDeleteCourier) {
      onDeleteCourier(courier);
    }
    setShowDeleteConfirm(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent className="sm:max-w-[95vw] md:max-w-[800px] max-h-[95vh] overflow-y-auto p-6">
          <DialogHeader className="pb-4 border-b border-border/30 mb-4">
            <DialogTitle className="sr-only">Информация о курьере</DialogTitle>
            <DialogDescription className="sr-only">Детальная информация о курьере</DialogDescription>
            <CourierDetailsHeader courier={courier} />
          </DialogHeader>
          
          <CourierDetailsCard courier={courier} />
          
          <Tabs defaultValue="rentals" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="rentals">История аренд</TabsTrigger>
              <TabsTrigger value="payments">История платежей</TabsTrigger>
            </TabsList>
            <TabsContent value="rentals" className="mt-4">
              <RentalHistoryTab courierId={courier.id.toString()} />
            </TabsContent>
            <TabsContent value="payments" className="mt-4">
              <PaymentHistoryTab courierId={courier.id.toString()} />
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6 flex gap-2 flex-row justify-between">
            <Button 
              variant="destructive"
              size="sm"
              className="flex items-center gap-2"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
              Удалить клиента
            </Button>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Закрыть
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Вы уверены, что хотите удалить клиента?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Будут удалены все данные клиента, включая историю аренд и платежей.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
